import { extraRotationPlaceholderDeliveryId } from './placeholderDeliveryIds';
import { VehicleRowVM } from './vehicleRowVM';

/**
 * 回転の追加・削除は勤務 (delivery) ごとに扱う。
 *
 * truckId で束ねていたため、1 台に 2 つの勤務が乗るとき別の勤務に回転が足されていた
 * （報告: 矢野 利秀の「回転追加」がドライバー未設定に入った）。回転は勤務の中の概念。
 */

// 追加回転の次番号 = サーバ最大とローカル最大の +1（Presenter.tsx:1773-1779）。
export function nextRotationNumber(
  sourceRowsRaw: VehicleRowVM[],
  existing: number[],
  deliveryId: number,
): number {
  const serverMax = Math.max(
    0,
    ...sourceRowsRaw.filter((r) => r.deliveryId === deliveryId).map((r) => r.rotationNumber),
  );
  const localMax = Math.max(0, ...existing);
  return Math.max(serverMax, localMax) + 1;
}

// サーバで既に存在する回転を extraRotations から落とす（Presenter.tsx:624-644）。
// 変化が無ければ同一参照を返す（不要な再レンダー防止）。
export function dropServerExistingRotations(
  extraRotations: Map<number, number[]>,
  sourceRowsRaw: VehicleRowVM[],
): Map<number, number[]> {
  if (extraRotations.size === 0) return extraRotations;
  const serverRotationsByDelivery = new Map<number, Set<number>>();
  for (const r of sourceRowsRaw) {
    const set = serverRotationsByDelivery.get(r.deliveryId);
    if (set) set.add(r.rotationNumber);
    else serverRotationsByDelivery.set(r.deliveryId, new Set([r.rotationNumber]));
  }
  const next = new Map<number, number[]>();
  let changed = false;
  for (const [deliveryId, rns] of extraRotations) {
    const serverSet = serverRotationsByDelivery.get(deliveryId);
    const filtered = rns.filter((rn) => !(serverSet && serverSet.has(rn)));
    if (filtered.length !== rns.length) changed = true;
    if (filtered.length > 0) next.set(deliveryId, filtered);
  }
  return changed ? next : extraRotations;
}

/** `mergeExtraRotations` が行を呼び出し間で使い回すための入れ物。呼び出し側が `useRef` で持つ。 */
export type ExtraRotationRowCache = {
  /** `${追加先の deliveryId}-${回転番号}` → placeholder 行 */
  rows: Map<string, VehicleRowVM>;
  /**
   * 入力行 → `totalRotations` を書き換えた行。
   *
   * placeholder だけ据え置いても足りない。`buildVehicleRowGroups` のグループ再利用条件
   * (`sameRows`) は行の参照比較で、グループには回転数を書き換えた実配送行も入る。そちらが
   * 毎回新品だとグループごと作り直され、placeholder を据え置いた意味が無くなる。
   * 入力行が捨てられればエントリも消えるよう `WeakMap` で持つ。
   */
  updated: WeakMap<VehicleRowVM, VehicleRowVM>;
};

export function createExtraRotationRowCache(): ExtraRotationRowCache {
  return { rows: new Map(), updated: new WeakMap() };
}

const groupKeyOf = (r: VehicleRowVM) => r.extraOfDeliveryId ?? r.deliveryId;

// placeholder 行の内容が前回と同じか。
//
// Why not 静的なキー配列で比べる: フィールドが増えたときに漏れると「変わったのに前回の行を
// 返す」= 表示が古いまま残る方向に倒れる。全キーを走査すれば、漏れは「余分に描く」方向で
// 済む（adapter の equalRowStates と同じ理由）。
// cards だけは配列で毎回作り直すので参照比較にならない。中身は (truckId, rotationNumber) から
// 決まり、その 2 つは走査対象に入っているので比較から外して差し支えない。
function sameExtraRotationRow(a: VehicleRowVM, b: VehicleRowVM): boolean {
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  for (const key of new Set([...Object.keys(left), ...Object.keys(right)])) {
    if (key === 'cards') continue;
    if (left[key] !== right[key]) return false;
  }
  return true;
}

// sourceRowsRaw に extraRotations をマージし空 placeholder 行を追加（Presenter.tsx:646-682）。
// placeholder は「再積み込み拠点」loading カード 1 枚のみ（unloading 無し＝地図線なし）。
//
// cache を渡すと placeholder 行の参照を呼び出し間で維持する。渡さないと毎回新品になり、
// buildVehicleRowGroups の再利用条件（入力 VM の要素ごとの参照が前回と同じ）が
// その行を含むグループで必ず崩れる（#298 で据え置いた制限）。
export function mergeExtraRotations(
  sourceRowsRaw: VehicleRowVM[],
  extraRotations: Map<number, number[]>,
  cache?: ExtraRotationRowCache,
): VehicleRowVM[] {
  if (extraRotations.size === 0) return sourceRowsRaw;

  // 追加先の勤務から truck と乗務者を引く。truckId は placeholder カードの placeKey と
  // グループ解決 (adapter / vehicleRowVM) に、driverId は行の乗務者表示に要る
  // （引き継がないと「ドライバー未設定」と出て、別の人の勤務に足したように見える）
  const sourceByDelivery = new Map<number, VehicleRowVM>();
  for (const r of sourceRowsRaw) {
    if (!sourceByDelivery.has(r.deliveryId)) sourceByDelivery.set(r.deliveryId, r);
  }

  // 総回転数も勤務ごと。truck 全体で数えると、別の勤務に足した回転で
  // 1 人目の「◯/N 回転目」まで増える。
  //
  // placeholder を作る前に確定させる。後段の map で足すと placeholder が必ず新しい
  // オブジェクトになり、下の使い回しが効かない。
  const rotCount = new Map<number, number>();
  for (const r of sourceRowsRaw) {
    rotCount.set(groupKeyOf(r), (rotCount.get(groupKeyOf(r)) ?? 0) + 1);
  }
  for (const [deliveryId, rns] of extraRotations) {
    // 下のループと同じ条件で数える（追加元が消えた勤務の回転は行にならない）
    if (!sourceByDelivery.has(deliveryId)) continue;
    rotCount.set(deliveryId, (rotCount.get(deliveryId) ?? 0) + rns.length);
  }

  // 使い回した行だけを次のキャッシュへ移す。消えた回転のエントリを残すと溜まり続ける
  const nextCacheRows = new Map<string, VehicleRowVM>();
  const extras: VehicleRowVM[] = [];
  for (const [deliveryId, rns] of extraRotations) {
    const origin = sourceByDelivery.get(deliveryId);
    // 追加元の勤務が消えていれば足せない（削除された勤務に回転だけ残さない）
    if (!origin) continue;
    const truckId = origin.truckId;
    for (const rn of rns) {
      const candidate: VehicleRowVM = {
        deliveryId: extraRotationPlaceholderDeliveryId(deliveryId, rn),
        truckId,
        rotationNumber: rn,
        totalRotations: rotCount.get(deliveryId) ?? 1,
        driverId: origin.driverId,
        driverColor: '#9e9e9e',
        truckColor: null,
        totalItemCount: 0,
        cards: [{
          kind: 'loading',
          placeKey: `reload-${truckId}-${rn}`,
          placeName: '再積み込み拠点',
          timeLabel: null,
          itemCount: 0,
          operations: [],
          legRuns: [],
          isRouteDeviation: false,
          isAutoAssigned: false,
        }],
        isPlaceholderRow: true,
        isExtraRotation: true,
        // どの勤務に足した回転かを覚えておく。deliveryId は負のダミーなので、
        // これが無いと追加先を後から引けない
        extraOfDeliveryId: deliveryId,
      };

      const cacheKey = `${deliveryId}-${rn}`;
      const previous = cache?.rows.get(cacheKey);
      const row = previous && sameExtraRotationRow(previous, candidate) ? previous : candidate;
      nextCacheRows.set(cacheKey, row);
      extras.push(row);
    }
  }
  if (cache) cache.rows = nextCacheRows;

  const merged: VehicleRowVM[] = [...sourceRowsRaw, ...extras];
  // 変わらない行は同じ参照を返す。参照が変わると memo 済みの行・グループが
  // 全部描き直される（「変わらないものは同一参照」という hideDeletingShifts と
  // 同じ規約。ただし維持する単位は違い、あちらは配列そのもの、ここは行 1 件ずつで、
  // 配列は map なので毎回新しくなる。配列側の再利用は buildVehicleRowGroups が持つ）
  return merged.map((r) => {
    const next = rotCount.get(groupKeyOf(r)) ?? 1;
    if (r.totalRotations === next) return r;
    const cached = cache?.updated.get(r);
    if (cached && cached.totalRotations === next) return cached;
    const updated = { ...r, totalRotations: next };
    cache?.updated.set(r, updated);
    return updated;
  });
}
